
import { useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function CareersPage() {
  useEffect(() => {
    // Update page title and meta description
    document.title = 'Careers at OED Glamping - Join Our Growing Team in Alabama';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Join OED Glamping\'s growing team in Talladega, Alabama. Explore career opportunities in glamping hospitality, project management, sustainability, and operations. Competitive benefits and meaningful work in eco-luxury tourism.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'OED Glamping careers, Alabama jobs, hospitality careers, glamping jobs, Talladega employment, eco-tourism careers, outdoor hospitality jobs, sustainable tourism careers');
    }
    
    // Add structured data for job postings
    const jobPostingsSchema = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": "Multiple Positions Available",
      "description": "Join OED Glamping's growing team with opportunities in project management, hospitality operations, sustainability, and more.",
      "hiringOrganization": {
        "@type": "Organization",
        "name": "OED Glamping",
        "sameAs": "https://oedglamping.com"
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Talladega",
          "addressRegion": "AL",
          "addressCountry": "US"
        }
      },
      "employmentType": ["FULL_TIME", "PART_TIME"],
      "datePosted": "2024-12-19"
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jobPostingsSchema);
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const jobOpenings = [
    {
      title: 'Senior Project Manager',
      department: 'Development',
      location: 'Talladega, AL',
      type: 'Full-time',
      description: 'Lead glamping development projects from conception to completion, managing timelines, budgets, and stakeholder relationships.'
    },
    {
      title: 'Hospitality Operations Manager',
      department: 'Operations',
      location: 'Multiple Locations',
      type: 'Full-time',
      description: 'Oversee daily operations of glamping sites, ensuring exceptional guest experiences and operational efficiency.'
    },
    {
      title: 'Sustainability Coordinator',
      department: 'Environmental',
      location: 'Remote/Talladega, AL',
      type: 'Full-time',
      description: 'Develop and implement environmental sustainability programs across all OED properties and developments.'
    },
    {
      title: 'Investment Relations Specialist',
      department: 'Finance',
      location: 'Talladega, AL',
      type: 'Full-time',
      description: 'Manage investor relationships, prepare financial reports, and support fundraising activities for new developments.'
    },
    {
      title: 'Site Maintenance Technician',
      department: 'Maintenance',
      location: 'Various Sites',
      type: 'Full-time',
      description: 'Maintain glamping facilities, perform routine inspections, and ensure all accommodations meet quality standards.'
    },
    {
      title: 'Guest Experience Coordinator',
      department: 'Hospitality',
      location: 'Multiple Locations',
      type: 'Part-time',
      description: 'Provide exceptional guest services, coordinate activities, and ensure memorable glamping experiences.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <header>
        <section 
          className="relative h-96 bg-cover bg-center bg-no-repeat flex items-center justify-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/b99ed633a3824a30d12abbcefa0da72a.jpg')`
          }}
        >
          <div className="text-center text-white max-w-4xl mx-auto px-4">
            <h1 className="text-5xl font-bold mb-6">Join Our Team</h1>
            <p className="text-xl leading-relaxed">
              Build your career with OED Glamping and help create extraordinary experiences while making a positive impact on the environment and communities.
            </p>
          </div>
        </section>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Why Work With Us */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Work With OED Glamping?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join a growing company that values innovation, sustainability, and creating meaningful experiences in nature's most beautiful settings.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <article className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-rocket-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Growth Opportunities</h3>
              <p className="text-gray-600">Advance your career in a rapidly expanding industry with clear promotion paths.</p>
            </article>
            
            <article className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Meaningful Work</h3>
              <p className="text-gray-600">Create lasting memories for guests while preserving natural environments.</p>
            </article>
            
            <article className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Great Team</h3>
              <p className="text-gray-600">Work alongside passionate professionals who share your values and vision.</p>
            </article>
            
            <article className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-gift-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Great Benefits</h3>
              <p className="text-gray-600">Competitive compensation, health benefits, and unique perks including glamping discounts.</p>
            </article>
          </div>
        </section>

        {/* Current Openings */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Current Openings</h2>
          
          <div className="space-y-6">
            {jobOpenings.map((job, index) => (
              <article key={index} className="bg-white border border-gray-200 rounded-lg p-8 hover:shadow-lg transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <h3 className="text-2xl font-bold text-gray-900">{job.title}</h3>
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium">
                        {job.department}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-4">
                      <div className="flex items-center space-x-2">
                        <i className="ri-map-pin-line"></i>
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <i className="ri-time-line"></i>
                        <span>{job.type}</span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4">{job.description}</p>
                  </div>
                  
                  <div className="lg:ml-8">
                    <Link 
                      to="/careers/application"
                      className="w-full lg:w-auto bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap inline-block text-center"
                    >
                      Apply Now
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-16 bg-gray-50 rounded-lg p-12">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Employee Benefits</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <article className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Health & Wellness</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Comprehensive health insurance</li>
                <li>• Dental and vision coverage</li>
                <li>• Mental health support</li>
                <li>• Wellness programs</li>
              </ul>
            </article>
            
            <article className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Financial Benefits</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Competitive salary</li>
                <li>• Performance bonuses</li>
                <li>• 401(k) with company match</li>
                <li>• Stock options</li>
              </ul>
            </article>
            
            <article className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Time Off</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Generous PTO policy</li>
                <li>• Paid holidays</li>
                <li>• Sabbatical opportunities</li>
                <li>• Flexible work arrangements</li>
              </ul>
            </article>
            
            <article className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-graduation-cap-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Development</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Professional development budget</li>
                <li>• Conference attendance</li>
                <li>• Mentorship programs</li>
                <li>• Skills training</li>
              </ul>
            </article>
            
            <article className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tent-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Unique Perks</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Free glamping stays</li>
                <li>• Employee discounts</li>
                <li>• Team retreats</li>
                <li>• Outdoor gear allowance</li>
              </ul>
            </article>
            
            <article className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-community-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Community</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Volunteer time off</li>
                <li>• Community involvement</li>
                <li>• Environmental initiatives</li>
                <li>• Team building events</li>
              </ul>
            </article>
          </div>
        </section>

        {/* Application Process */}
        <section className="text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">Application Process</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <article className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-xl font-bold">1</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Apply Online</h3>
              <p className="text-gray-600">Submit your application and resume through our online portal.</p>
            </article>
            
            <article className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-xl font-bold">2</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Initial Review</h3>
              <p className="text-gray-600">Our HR team reviews your application and qualifications.</p>
            </article>
            
            <article className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-xl font-bold">3</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Interview Process</h3>
              <p className="text-gray-600">Phone screening followed by in-person or virtual interviews.</p>
            </article>
            
            <article className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-xl font-bold">4</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Welcome Aboard</h3>
              <p className="text-gray-600">Onboarding and orientation to get you started on your new journey.</p>
            </article>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
